import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { fetchTripDetail, setMainJourney, generateAlternative, generateInitialAlternatives, deleteTrip, planAlternatives, updateTrip, deleteAlternatives, copyTrip } from '../features/my-trips/api';
import { TripDetail } from '../features/my-trips/types';
import { Strategy, AlternativePlan } from '../features/my-trips/strategies';
import TripDetailNav from '../features/my-trips/components/TripDetailNav';
import MetaLine from '../features/my-trips/components/MetaLine';
import StatusPill from '../features/my-trips/components/StatusPill';
import { resolveDisplayStatus } from '../features/my-trips/utils';
import AlternativesView from '../features/my-trips/components/AlternativesView';
import { RouteFocusMap } from '../features/my-trips/components/RouteFocus';
import DetailSplitLayout, { useDetailSplit } from '../features/my-trips/components/DetailSplitLayout';
import AlternativeGenerator from '../features/my-trips/components/AlternativeGenerator';
import ApproxNote from '../features/my-trips/components/ApproxNote';
import LLMLoadingSpinner from '../components/LLMLoadingSpinner';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { deleteJourney, renameJourney, copyJourney } from '../features/journeys/api';
import NameDialog from '../components/NameDialog';
import { KebabIcons } from '../features/my-trips/components/KebabMenu';
import TripShareDialog from '../features/my-trips/components/TripShareDialog';
import { fetchSharePath } from '../features/share/api';
import ShareTag from '../features/my-trips/components/ShareTag';
import NotAvailableCard from '../features/my-trips/components/NotAvailableCard';
import { useAuth } from '../features/auth/context';
import { openGuestWall, guestWallJustOpened } from '../features/auth/guest';
import { trackJourneyConversion } from '../shared/gtag';
import GuestStatusStrip from '../features/auth/GuestStatusStrip';
import Button from '../components/ui/button';
import { useDocumentTitle } from '../shared/useDocumentTitle';

// Trip detail page (design-handoff.md C.2). Phase 2: compare a trip's
// alternatives and pick a main one. Brand bar is the app's global Header.

// Desktop: "Explore more alternatives" stays pinned to the bottom of the left
// column while the alternatives list scrolls above it; at the end of the list
// it settles in place. var(--bg) hides the cards scrolling underneath.
const exploreBarStyle: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  zIndex: 5,
  background: 'var(--bg)',
  borderTop: '1px solid rgba(20,40,30,0.10)',
  padding: '12px 0 16px',
};

// Axios/fetch abort surfaces as a canceled error — used to skip the error toast
// when the user themselves cancelled generation.
const isAbortError = (e: any): boolean =>
  e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';

const TripDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast: showToastRaw } = useToast();
  // Same guest-wall toast suppression as JourneyDetailPage: the wall dialog
  // is the feedback for a gated action; don't stack an error toast on it.
  const showToast: typeof showToastRaw = useCallback((opts) => {
    if (opts?.type === 'error' && guestWallJustOpened()) return;
    showToastRaw(opts);
  }, [showToastRaw]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Anonymous access: under RequireAuthOrGuest, no user means a guest session.
  // Guests view and compare; every edit affordance opens the signup wall.
  const { user } = useAuth();
  const isGuest = !user;

  const [detail, setDetail] = useState<TripDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // 404 on the initial load: deleted, or another account's trip (the API
  // returns the same 404 for both). Gets the NotAvailableCard, no error toast.
  const [notFound, setNotFound] = useState(false);
  // Tab/history title: the trip's name once loaded (see shared/useDocumentTitle).
  useDocumentTitle(detail?.title);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Chip being hovered: the RouteFocus map previews that alternative's route.
  const [hoverPreviewId, setHoverPreviewId] = useState<string | null>(null);
  // Planner decision (which alternatives to offer + why). Server-cached per
  // brief version, so fetching it on load is cheap; failure is non-fatal (the
  // Explore tags simply fall back to the local heuristic).
  const [plan, setPlan] = useState<AlternativePlan | null>(null);
  const [settingMain, setSettingMain] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Initial-set streaming (backlog #33): true from the first alternative
  // appearing until the whole set has been generated — drives the inline
  // "Jody is still planning more routes…" hint under the chips.
  const [planningMore, setPlanningMore] = useState(false);
  // Cancel plumbing for generation: abort the in-flight request when the user
  // hits Cancel on the loading overlay (same client-side abort semantics as the
  // legacy Journey Planner — the server may still finish; it just surfaces on
  // the next load).
  const generateAbortRef = useRef<AbortController | null>(null);
  // Chip kebab → Delete: hold the pending alternative until the shared
  // ConfirmationDialog confirms (destructive-confirm convention).
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Trip-level delete (kebab in the top nav) — separate from the per-alternative
  // delete above.
  const [confirmTripDelete, setConfirmTripDelete] = useState(false);
  const [deletingTrip, setDeletingTrip] = useState(false);
  // A ?focus=<journeyId> (e.g. arriving after creating a route) selects that
  // alternative on first load, then defers to normal behaviour.
  const focusConsumedRef = useRef(false);
  // ?generate=default auto-fires one alternative right after trip creation, once.
  const autoFiredRef = useRef(false);
  // Trip sharing v1: the share dialog (kebab → Share, or ?share=1 from the
  // owner notification email's "Manage sharing" link).
  const [shareOpen, setShareOpen] = useState(false);
  const shareParamConsumedRef = useRef(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchTripDetail(id);
      setDetail(data);
      const ids = data.alternatives.map((a) => a.id);
      // Read the initial focus straight from the URL (not the searchParams state)
      // so the focus→URL sync effect below doesn't change `load`'s identity and
      // re-trigger a refetch on every selection.
      const focus = new URLSearchParams(window.location.search).get('focus');
      // Consume the focus param outside the state updater — updaters must be
      // pure (StrictMode double-invokes them), so the ref mutation can't live
      // inside one.
      if (!focusConsumedRef.current && focus && ids.includes(focus)) {
        focusConsumedRef.current = true;
        setSelectedId(focus);
        return;
      }
      // Keep the current selection if still present, else default to main/first.
      setSelectedId((prev) => {
        if (prev && ids.includes(prev)) return prev;
        return data.mainJourneyId && ids.includes(data.mainJourneyId)
          ? data.mainJourneyId
          : data.alternatives[0]?.id ?? null;
      });
    } catch (e: any) {
      // 404 is a normal outcome (wrong account, deleted, mistyped URL): the
      // page renders the explanation card — a toast on top would be noise
      // (and StrictMode's double effect used to fire it twice). A 401 whose
      // handling the auth interceptor didn't already take over (e.g. a stale
      // guest token that was just cleared) gets the same card: to this
      // browser the trip is equally unavailable.
      const status = e?.response?.status;
      if (status === 404 || (status === 401 && !guestWallJustOpened())) {
        // Someone else's trip: if its trip is shared, open the shared view
        // instead of a wall (Paulo 2026-10-03). Signed-out visitors are sent
        // there earlier, by RequireAuthOrGuest.
        const sharePath = await fetchSharePath({ tripId: id });
        if (sharePath) {
          navigate(sharePath + window.location.search, { replace: true });
          return;
        }
        setNotFound(true);
      } else showToast({ title: 'Error', message: 'Failed to load this trip.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch (or compute once server-side) the alternative plan for this trip.
  const loadPlan = useCallback(async () => {
    if (!id) return;
    try {
      setPlan(await planAlternatives(id));
    } catch {
      setPlan(null); // non-fatal: tags fall back to the local heuristic
    }
  }, [id]);
  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Reflect the focused alternative in the URL (?focus=<id>, replace) so the
  // browser Back button returns to this trip with that alternative focused.
  // replace (not push) keeps each chip click out of the history stack.
  useEffect(() => {
    if (!selectedId) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('focus') === selectedId) return;
    sp.set('focus', selectedId);
    setSearchParams(sp, { replace: true });
  }, [selectedId, setSearchParams]);

  const selected = useMemo(
    () => detail?.alternatives.find((a) => a.id === selectedId) ?? null,
    [detail, selectedId],
  );
  const hoverPreview = useMemo(
    () => detail?.alternatives.find((a) => a.id === hoverPreviewId) ?? null,
    [detail, hoverPreviewId],
  );
  // Desktop (DetailSplitLayout): the selected route's map is the right-hand
  // panel, and the alternatives stack on the left as full RouteFocus cards
  // instead of the chips strip + one focus card.
  const split = useDetailSplit();

  // "Why this route" line for an alternative the planner chose (stamped with
  // its journey id when the initial set was generated).
  const planReasonFor = useCallback(
    (journeyId: string) => plan?.initial.find((e) => e.journeyId === journeyId)?.reason ?? null,
    [plan],
  );

  // Guest locked card (anonymous access v1 §8): the planner proposed this
  // route but the guest tier didn't build it — real title + reason from the
  // plan, no fake numbers. Building it is a signup carrot. Exactly ONE card:
  // guests get 2 built alternatives and one locked (Paulo 2026-08-30).
  const lockedEntries = useMemo(
    () => (isGuest && plan?.initial ? plan.initial.filter((e) => !e.journeyId).slice(0, 1) : []),
    [isGuest, plan],
  );

  // Alternative costs are estimated asynchronously after generation; while any
  // chip is missing its price, poll the trip so they land on the chips by
  // themselves (GET /trips/:id also re-kicks pricing for unpriced alternatives).
  // Same interval/cap as the journey detail page's pricing poll.
  const pricingPending = useMemo(
    () => (detail?.alternatives || []).some((a) => a.costCents == null),
    [detail],
  );
  useEffect(() => {
    if (!pricingPending || !id) return;
    let attempts = 0;
    const MAX_ATTEMPTS = 12; // ~1 min at 5s intervals
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const fresh = await fetchTripDetail(id);
        setDetail(fresh);
        const stillPending = fresh.alternatives.some((a) => a.costCents == null);
        if (!stillPending || attempts >= MAX_ATTEMPTS) clearInterval(timer);
      } catch {
        if (attempts >= MAX_ATTEMPTS) clearInterval(timer);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [pricingPending, id]);

  // Kebab → Rename, for the trip itself and for any alternative. One dialog
  // serves both; `renameTarget.kind` decides which endpoint commits it.
  const [renameTarget, setRenameTarget] = useState<null | { kind: 'trip' | 'alternative' | 'copy' | 'tripCopy'; id: string; title: string }>(null);
  const [renaming, setRenaming] = useState(false);
  const handleRename = useCallback(async (title: string) => {
    if (!renameTarget) return;
    setRenaming(true);
    try {
      if (renameTarget.kind === 'trip') await updateTrip(renameTarget.id, { title });
      else if (renameTarget.kind === 'tripCopy') {
        // Deep copy (trip + ALL alternatives, deterministic rows — Paulo
        // 2026-09-14). Lands on the new trip, like the journey copy lands
        // on the new journey.
        const created = await copyTrip(renameTarget.id, title);
        setRenameTarget(null);
        showToast({ title: 'Copied', message: `"${title}" is ready, with all its routes.`, type: 'success', duration: 3000 });
        navigate(`/trips/${encodeURIComponent(created.tripId)}`);
        return;
      } else if (renameTarget.kind === 'copy') {
        // Copying opens the copy, here as on the journey page (Paulo
        // 2026-08-28): you asked for a new route, so land on it rather than
        // hunting for a fresh chip in the strip.
        const created = await copyJourney(renameTarget.id, title);
        setRenameTarget(null);
        showToast({ title: 'Copied', message: `"${title}" is ready to edit.`, type: 'success', duration: 3000 });
        navigate(`/journeys/${encodeURIComponent(created.id)}`);
        return;
      } else {
        const res = await renameJourney(renameTarget.id, title);
        if (!res.success) throw new Error(res.error?.message || 'Rename failed');
      }
      setRenameTarget(null);
      showToast({ title: 'Renamed', message: renameTarget.kind === 'trip' ? 'Trip name updated.' : 'Alternative renamed.', type: 'success', duration: 3000 });
      await load();
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || e?.message || 'Could not rename. Please try again.', type: 'error' });
    } finally {
      setRenaming(false);
    }
  }, [renameTarget, showToast, load, navigate]);

  // Bulk alternative deletes. One endpoint serves both: no keeper = "delete
  // all", a keeper = "delete the others". Server-side it is one transaction and
  // re-resolves the trip's main journey, so the survivor is promoted.
  const [confirmBulk, setConfirmBulk] = useState<null | { keepJourneyId?: string; count: number; keepTitle?: string }>(null);
  const handleBulkDelete = useCallback(async () => {
    if (!id || !confirmBulk) return;
    const { keepJourneyId } = confirmBulk;
    setConfirmBulk(null);
    try {
      const res = await deleteAlternatives(id, keepJourneyId);
      showToast({
        title: 'Deleted',
        message: res.deletedCount === 1 ? 'One alternative was deleted.' : `${res.deletedCount} alternatives were deleted.`,
        type: 'success',
        duration: 3000,
      });
      await load();
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not delete the alternatives. Please try again.', type: 'error' });
    }
  }, [id, confirmBulk, showToast, load]);

  const handleSetMain = useCallback(async (journeyId: string) => {
    if (!id) return;
    setSettingMain(true);
    try {
      await setMainJourney(id, journeyId);
      showToast({ title: 'Updated', message: 'Main route set.', type: 'success' });
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || 'Failed to set main route';
      showToast({ title: 'Error', message: msg, type: 'error' });
    } finally {
      setSettingMain(false);
    }
  }, [id, load, showToast]);

  // Generate one alternative from the trip's brief + the chosen strategy. The
  // brief is the source of truth — no second form. The journey is created,
  // attached, and (if first) set as main server-side; we just reload + select it.
  const handlePickStrategy = useCallback(async (strategy: Strategy) => {
    if (!id || generating) return;
    const controller = new AbortController();
    generateAbortRef.current = controller;
    setGenerating(true);
    try {
      const journey = await generateAlternative(id, strategy, controller.signal);
      showToast({ title: 'Route ready', message: 'A new alternative was generated.', type: 'success' });
      await load();
      if (journey?.id) setSelectedId(journey.id);
    } catch (e: any) {
      if (isAbortError(e)) return; // user cancelled — handleCancelGeneration already toasted
      const data = e?.response?.data;
      const msg =
        (data?.error && typeof data.error === 'object' ? data.error.message : data?.error) ||
        e?.message ||
        'Failed to generate a route.';
      // An AI usage limit (backend middleware/aiUsageLimits.js) is a notice, not an error.
      const limited = data?.code === 'ai_daily_limit' || data?.code === 'ai_rate_limited';
      showToast({ title: limited ? 'Limit reached' : 'Error', message: msg, type: limited ? 'info' : 'error' });
    } finally {
      generateAbortRef.current = null;
      setGenerating(false);
    }
  }, [id, generating, load, showToast]);

  // Auto-fire on trip creation: generate the whole initial SET in one call.
  // The server persists alternatives PROGRESSIVELY (backlog #33): the mix +
  // derived variants commit first, each remaining generation as it completes.
  // So while the request runs we poll the trip and stream the chips in — the
  // full-screen spinner drops at the FIRST alternative; the "planning more"
  // hint (below the switcher) stays until the request settles.
  const handleGenerateInitial = useCallback(async () => {
    if (!id || generating) return;
    const controller = new AbortController();
    generateAbortRef.current = controller;
    setGenerating(true);
    let sawFirst = false;
    const timer = setInterval(async () => {
      try {
        const fresh = await fetchTripDetail(id);
        if (fresh.alternatives.length === 0) return;
        setDetail(fresh);
        if (!sawFirst) {
          sawFirst = true;
          setSelectedId(fresh.mainJourneyId ?? fresh.alternatives[0].id);
          setGenerating(false); // spinner → page; chips keep streaming in
          setPlanningMore(true);
          loadPlan(); // batch-1 reasons are stamped server-side already
        }
      } catch {
        // Transient poll failure — the final load() below settles the page.
      }
    }, 3000);
    try {
      const journeys = await generateInitialAlternatives(id, controller.signal);
      // Google Ads "journey generated" conversion: one per trip planned, guests
      // included (docs/integrations/google-ads.md). Only here on purpose: the
      // per-strategy picker and the post-edit regeneration rework an existing
      // trip; a guest re-fire on a populated trip is rejected server-side (409),
      // and the ?generate=default effect skips trips that already have alternatives.
      trackJourneyConversion();
      showToast({ title: 'Routes ready', message: 'Your first alternatives are ready.', type: 'success' });
      await load();
      await loadPlan(); // pick up the final journeyId stamps (reason subtitles)
      if (journeys?.[0]?.id) setSelectedId(journeys[0].id);
    } catch (e: any) {
      if (isAbortError(e)) return; // user cancelled — handleCancelGeneration already toasted
      const data = e?.response?.data;
      const msg =
        (data?.error && typeof data.error === 'object' ? data.error.message : data?.error) ||
        e?.message ||
        'Failed to generate routes.';
      // An AI usage limit (backend middleware/aiUsageLimits.js) is a notice, not an error.
      const limited = data?.code === 'ai_daily_limit' || data?.code === 'ai_rate_limited';
      showToast({ title: limited ? 'Limit reached' : 'Error', message: msg, type: limited ? 'info' : 'error' });
    } finally {
      clearInterval(timer);
      generateAbortRef.current = null;
      setGenerating(false);
      setPlanningMore(false);
    }
  }, [id, generating, load, loadPlan, showToast]);

  // Cancel an in-flight generation from the loading overlay. Aborts the request
  // and drops the overlay; the catch guard (isAbortError) skips the error toast.
  const handleCancelGeneration = useCallback(() => {
    generateAbortRef.current?.abort();
    generateAbortRef.current = null;
    setGenerating(false);
    showToast({ title: 'Generation stopped', message: 'You can pick a route again whenever you like.', type: 'info', duration: 4000 });
  }, [showToast]);

  // Phase 3: open the real shared Journey detail page (was the ?open= drawer interim).
  const handleSeeInFull = useCallback((journeyId: string) => {
    navigate(`/journeys/${encodeURIComponent(journeyId)}`);
  }, [navigate]);

  // Delete an alternative from a chip's kebab menu. Reuses the same deleteJourney
  // API + toast behaviour as the Journey detail page's delete; here we reload the
  // trip (load() re-selects main/first when the focused one is gone) instead of
  // navigating away.
  const handleDeleteAlternative = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await deleteJourney(pendingDelete.id);
      if (!res.success) throw new Error(res.error?.message || 'Delete failed');
      showToast({ title: 'Deleted', message: 'Alternative deleted.', type: 'success' });
      setPendingDelete(null);
      await load();
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.message || 'Failed to delete alternative', type: 'error' });
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, load, showToast]);

  // Delete the whole trip (and its alternatives), confirmed via the shared
  // dialog, then return to the trips list.
  const handleDeleteTrip = useCallback(async () => {
    if (!detail) return;
    setDeletingTrip(true);
    try {
      await deleteTrip(detail.id, 'delete');
      showToast({ title: 'Deleted', message: 'Trip deleted.', type: 'success' });
      navigate('/trips');
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || e?.message || 'Failed to delete trip', type: 'error' });
      setDeletingTrip(false);
    }
  }, [detail, navigate, showToast]);

  // ?share=1 (owner email "Manage sharing" link) opens the share dialog once,
  // then strips the param so refreshes don't reopen it.
  useEffect(() => {
    if (shareParamConsumedRef.current || loading || !detail) return;
    if (searchParams.get('share') !== '1') return;
    shareParamConsumedRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('share');
    setSearchParams(next, { replace: true });
    setShareOpen(true);
  }, [loading, detail, searchParams, setSearchParams]);

  // Auto-fire one default alternative right after trip creation (?generate=default),
  // so a freshly created trip is never an empty dead-end. Runs once (ref guard,
  // StrictMode-safe), only when the brief is generatable, and strips the param so
  // a manual refresh won't regenerate.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (loading || !detail) return;
    if (searchParams.get('generate') !== 'default') return;
    autoFiredRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('generate');
    setSearchParams(next, { replace: true });
    if (detail.alternatives.length > 0) return; // already has one — nothing to do
    const p = detail.params;
    const hasBrief = !!p?.origin?.city && Array.isArray(p?.destinations) && p.destinations.some((d) => !!d.city);
    if (!hasBrief) return; // incomplete brief — show the picker, don't error on landing
    handleGenerateInitial();
  }, [loading, detail, searchParams, setSearchParams, handleGenerateInitial]);

  // What an alternative's ⋮ menu does, on the chips (phones, tablets) and on
  // the desktop cards alike. Guests get the signup wall instead.
  const findAlt = (jid: string) => detail?.alternatives.find((x) => x.id === jid);
  const alternativeActions = {
    onDelete: isGuest ? () => openGuestWall() : (jid: string) => {
      setPendingDelete({ id: jid, title: findAlt(jid)?.title || 'this alternative' });
    },
    onRename: isGuest ? () => openGuestWall() : (jid: string) => {
      setRenameTarget({ kind: 'alternative', id: jid, title: findAlt(jid)?.title || '' });
    },
    onCopy: isGuest ? () => openGuestWall() : (jid: string) => {
      setRenameTarget({ kind: 'copy', id: jid, title: `Copy of ${findAlt(jid)?.title || 'route'}` });
    },
    onSetMain: isGuest ? () => openGuestWall() : handleSetMain,
    onDeleteOthers: isGuest ? () => openGuestWall() : (jid: string) => {
      setConfirmBulk({ keepJourneyId: jid, count: (detail?.alternatives.length ?? 1) - 1, keepTitle: findAlt(jid)?.title || 'this alternative' });
    },
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <DetailSplitLayout
        map={split && selected ? (
          <RouteFocusMap alt={selected} previewAlt={hoverPreview} onSeeDetails={handleSeeInFull} fill />
        ) : null}
      >
        {loading ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading trip…</div>
        ) : notFound ? (
          <NotAvailableCard kind="trip" isGuest={isGuest} />
        ) : !detail ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Trip not found.</div>
        ) : (
          <>
            <TripDetailNav
              title={detail.title}
              onBack={() => (isGuest ? navigate('/') : navigate('/trips'))}
              menuAriaLabel="Trip options"
              // Guests get a menu too, with only Share: sharing is never gated
              // on signup (spec §7 — it is the acquisition loop).
              menuItems={isGuest ? [
                { label: 'Share', icon: KebabIcons.share, onSelect: () => setShareOpen(true) },
              ] : [
                { label: 'Share', icon: KebabIcons.share, onSelect: () => setShareOpen(true) },
                { label: 'Rename', icon: KebabIcons.pencil, onSelect: () => setRenameTarget({ kind: 'trip', id: detail.id, title: detail.title }) },
                { label: 'Trip preferences', icon: KebabIcons.sliders, onSelect: () => navigate(`/trips/${encodeURIComponent(detail.id)}/edit`) },
                { label: 'Create copy', icon: KebabIcons.copy, onSelect: () => setRenameTarget({ kind: 'tripCopy', id: detail.id, title: `Copy of ${detail.title}` }) },
                {
                  label: 'Delete all alternatives',
                  icon: KebabIcons.trash,
                  tone: 'danger',
                  hidden: (detail.alternatives?.length || 0) === 0,
                  onSelect: () => setConfirmBulk({ count: detail.alternatives.length }),
                },
                { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', onSelect: () => setConfirmTripDelete(true) },
              ]}
            />

            {isGuest && <GuestStatusStrip />}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <StatusPill status={resolveDisplayStatus(detail.dateStart, detail.dateEnd, detail.stage)} />
              {detail.shareVisibility && detail.shareVisibility !== 'private' && (
                <ShareTag visibility={detail.shareVisibility} />
              )}
              <MetaLine
                dateStart={detail.dateStart}
                dateEnd={detail.dateEnd}
                alternativesCount={detail.alternativesCount}
                roundTrip={detail.params?.roundTrip !== false}
                durationPreset={detail.params?.durationPreset}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#16241d', margin: 0 }}>Saved alternatives</h2>
            </div>

            {detail.alternatives.length === 0 ? (
              <AlternativeGenerator
                view="cards"
                params={detail.params}
                // Guests can't call the per-strategy endpoint (JWT-only); any
                // pick re-fires the guest-allowed initial set instead — the
                // recovery path when ?generate=default failed or was skipped.
                onPick={isGuest ? () => handleGenerateInitial() : handlePickStrategy}
                busy={generating}
                heading="Generate your first route"
                subheading="Pick how to travel. We plan it from your trip details."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Desktop: the alternatives as stacked cards beside the map;
                    below the split, the chips strip and the focused route's
                    card (same component as the shared trip view). */}
                <AlternativesView
                  alternatives={detail.alternatives}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  hoverPreviewId={hoverPreviewId}
                  onHoverChange={setHoverPreviewId}
                  onOpen={handleSeeInFull}
                  menu={alternativeActions}
                  onSetMain={alternativeActions.onSetMain}
                  settingMain={settingMain}
                  reasonFor={planReasonFor}
                  split={split}
                  between={(
                    <>
                      {/* Initial set still streaming in (backlog #33). */}
                      {planningMore && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#6c7872', marginTop: -8 }}>
                          <style>{'@keyframes planmore-pulse{0%,80%,100%{opacity:.25}40%{opacity:1}}'}</style>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#157a55', animation: 'planmore-pulse 1.4s infinite' }} />
                          Jody is still planning more routes for you…
                        </div>
                      )}
                      {/* "Explore more alternatives" sits below the list (on phones and
                          tablets, above the focused route's card; on desktop pinned
                          to the bottom of the screen). Guests instead see the locked cards for the routes Jody
                          proposed but didn't build on the guest tier (spec §8). */}
                      {!isGuest && (
                        <div style={split ? exploreBarStyle : undefined}>
                          <AlternativeGenerator
                            view="compact"
                            params={detail.params}
                            onPick={handlePickStrategy}
                            busy={generating}
                            heading="Explore more alternatives"
                            defaultExpanded={detail.alternatives.length <= 2}
                            planExplore={plan?.explore}
                          />
                        </div>
                      )}
                      {isGuest && !generating && !planningMore && lockedEntries.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {lockedEntries.map((entry) => (
                            <div
                              key={entry.id}
                              style={{
                                background: '#fff',
                                border: '1px dashed #b9c6bf',
                                borderRadius: 14,
                                padding: '14px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                              }}
                            >
                              <span aria-hidden="true" style={{ fontSize: 18 }}>🔒</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#16241d' }}>
                                  {entry.title || 'One more route idea'}
                                </div>
                                <div style={{ fontSize: 12.5, color: '#6c7872', marginTop: 2 }}>{entry.reason}</div>
                              </div>
                              <Button
                                type="button"
                                onClick={() => openGuestWall('Sign up free and Jody builds this route for you right away.')}
                              >
                                Build this route
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                />
                <ApproxNote />
              </div>
            )}
          </>
        )}
      </DetailSplitLayout>

      {/* "Drafting" on purpose: the result is a starting point the user shapes,
          not a final answer (growth copy call, Paulo 2026-07-17). Cancel comes
          from the unified-overlay work (d1a25e1). */}
      <LLMLoadingSpinner message="Drafting your adventure…" onCancel={handleCancelGeneration} isVisible={generating} />

      {confirmBulk && (
        <ConfirmationDialog
          title={confirmBulk.keepJourneyId ? 'Delete the other alternatives?' : 'Delete all alternatives?'}
          message={confirmBulk.keepJourneyId
            ? `${confirmBulk.count === 1 ? 'The other alternative' : `The other ${confirmBulk.count} alternatives`} will be permanently deleted. "${confirmBulk.keepTitle}" stays, and becomes the main route.`
            : `All ${confirmBulk.count} ${confirmBulk.count === 1 ? 'alternative' : 'alternatives'} under this trip will be permanently deleted. The trip itself stays, ready for new routes.`}
          confirmText="Delete"
          onConfirm={handleBulkDelete}
          onCancel={() => setConfirmBulk(null)}
        />
      )}

      {renameTarget && (
        <NameDialog
          title={renameTarget.kind === 'copy' || renameTarget.kind === 'tripCopy' ? 'Create a copy' : renameTarget.kind === 'trip' ? 'Rename trip' : 'Rename alternative'}
          label={renameTarget.kind === 'trip' ? 'Trip name' : renameTarget.kind === 'tripCopy' ? 'Name for the trip copy' : renameTarget.kind === 'copy' ? 'Name for the copy' : 'Alternative name'}
          confirmText={renameTarget.kind === 'copy' || renameTarget.kind === 'tripCopy' ? 'Create copy' : 'Save'}
          initialValue={renameTarget.title}
          busy={renaming}
          onConfirm={handleRename}
          onCancel={() => setRenameTarget(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmationDialog
          title="Delete this alternative?"
          message={`"${pendingDelete.title}" will be permanently removed from this trip. This cannot be undone.`}
          confirmText={deleting ? 'Deleting…' : 'Delete'}
          confirmButtonStyle="danger"
          onConfirm={handleDeleteAlternative}
          onCancel={() => { if (!deleting) setPendingDelete(null); }}
        />
      )}

      {shareOpen && detail && (
        <TripShareDialog
          tripId={detail.id}
          tripTitle={detail.title}
          source="trip_detail"
          onClose={() => setShareOpen(false)}
          onVisibilityChange={(visibility) => setDetail((d) => (d ? { ...d, shareVisibility: visibility } : d))}
        />
      )}

      {confirmTripDelete && detail && (
        <ConfirmationDialog
          title="Delete this trip?"
          message={
            `"${detail.title}" and its ${detail.alternatives.length} ` +
            `route alternative${detail.alternatives.length === 1 ? '' : 's'} will be permanently deleted. This can't be undone.`
          }
          confirmText={deletingTrip ? 'Deleting…' : 'Delete'}
          confirmButtonStyle="danger"
          onConfirm={handleDeleteTrip}
          onCancel={() => { if (!deletingTrip) setConfirmTripDelete(false); }}
        />
      )}
    </div>
  );
};

export default TripDetailPage;
